import { CloseIcon, WarningIcon } from '@chakra-ui/icons';
import { Alert, Box, Button, Flex, IconButton, Text } from '@chakra-ui/react';
import { IconEyeOpen } from 'central-icons/IconEyeOpen';
import { startCase } from 'lodash';
import { useQuery } from 'urql';

import {
  clearImpersonation,
  selectImpersonatedUser,
  selectImpersonationActive,
  selectImpersonationOverrides,
  selectIsImpersonating,
  useGetLoggedInUser,
  useImpersonationSelector,
} from '@revelio/auth';
import {
  GetClientDetailsDocument,
  type GetClientDetailsQuery,
  type User,
} from '@revelio/data-access';

import {
  mapServerResponseToSelectOptions,
  pipelineOptions,
  reportOptionsLookup,
} from '../../utils/helpers';
import { ImpersonationBannerOverridesTooltip } from './ImpersonationBannerOverridesTooltip';
import {
  ImpersonationClientTooltipContent,
  ImpersonationUserTooltipContent,
} from './ImpersonationBannerTooltipContent';
import {
  getExpirationDate,
  getNormalizedTrialType,
  getTrialUserFlag,
} from './impersonationBanner.utils';

const normalizeTabs = (tabs: User['tabs'] | null | undefined) =>
  (tabs ?? [])
    .slice()
    .sort((left, right) => `${left}`.localeCompare(`${right}`));

const areTabsEqual = (
  left: User['tabs'] | null | undefined,
  right: User['tabs'] | null | undefined
) => {
  const normalizedLeft = normalizeTabs(left);
  const normalizedRight = normalizeTabs(right);

  if (normalizedLeft.length !== normalizedRight.length) {
    return false;
  }

  return normalizedLeft.every((tab, index) => tab === normalizedRight[index]);
};

const filterDefined = <T,>(
  items: Array<T | null | undefined> | null | undefined
): T[] | undefined => {
  if (!items) {
    return undefined;
  }

  return items.filter((item): item is T => item !== null && item !== undefined);
};

const areValuesEqual = <T,>(
  left: T[] | null | undefined,
  right: T[] | null | undefined
) => {
  const leftValues = left ?? [];
  const rightValues = right ?? [];

  if (leftValues.length !== rightValues.length) {
    return false;
  }

  return leftValues.every((value) => rightValues.includes(value));
};

export interface ImpersonationBannerProps {
  onReset?: () => void;
  onEditImpersonation?: () => void;
}

export function ImpersonationBanner({
  onReset,
  onEditImpersonation,
}: ImpersonationBannerProps) {
  const isImpersonating = useImpersonationSelector(selectIsImpersonating);
  const impersonatedUser = useImpersonationSelector(selectImpersonatedUser);
  const overrides = useImpersonationSelector(selectImpersonationOverrides);
  const isActive = useImpersonationSelector(selectImpersonationActive);
  const { realLoggedInUser } = useGetLoggedInUser();

  const handleReset = () => {
    clearImpersonation();
    onReset?.();
  };
  const handleResetClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    handleReset();
  };
  const isBannerClickable = Boolean(onEditImpersonation);
  const handleBannerClick = () => {
    onEditImpersonation?.();
  };
  const handleBannerKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (!isBannerClickable) {
      return;
    }

    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      onEditImpersonation?.();
    }
  };

  const baseUser = (
    isImpersonating && impersonatedUser ? impersonatedUser : realLoggedInUser
  ) as User | undefined;
  const realUser = realLoggedInUser as User | undefined;

  const baseTrialUser = getTrialUserFlag(baseUser);
  const baseTrialType = getNormalizedTrialType(baseUser);
  const baseExpirationDate = getExpirationDate(baseUser);
  const baseRole = baseUser?.role ?? undefined;

  const realTrialUser = getTrialUserFlag(realUser);
  const realTrialType = getNormalizedTrialType(realUser);
  const realExpirationDate = getExpirationDate(realUser);
  const realRole = realUser?.role ?? undefined;

  const baseClientName = baseUser?.client_name ?? undefined;
  const baseTabs = baseUser?.tabs ?? undefined;
  const baseLinkupPostings = baseUser?.linkup_postings ?? undefined;
  const realClientName = realUser?.client_name ?? undefined;
  const realTabs = realUser?.tabs ?? undefined;
  const realLinkupPostings = realUser?.linkup_postings ?? undefined;

  const resolvedTrialUser = overrides?.isTrialUser ?? baseTrialUser;
  const resolvedTrialType = overrides?.trialType ?? baseTrialType;
  const resolvedExpirationDate =
    overrides?.expirationDate ?? baseExpirationDate;
  const resolvedRole = overrides?.role ?? baseRole;

  const resolvedClientName = overrides?.clientName ?? baseClientName;
  const resolvedTabs = overrides?.tabs ?? baseTabs;
  const resolvedLinkupPostings =
    overrides?.linkupPostings ?? baseLinkupPostings;

  const [{ data: baseClientData }] = useQuery<GetClientDetailsQuery>({
    query: GetClientDetailsDocument,
    variables: {
      ids: baseClientName ? [baseClientName] : [],
    },
    pause: !isActive || !baseClientName,
  });
  const shouldFetchResolvedClient = Boolean(
    resolvedClientName && resolvedClientName !== baseClientName
  );
  const [{ data: resolvedClientData }] = useQuery<GetClientDetailsQuery>({
    query: GetClientDetailsDocument,
    variables: {
      ids: resolvedClientName ? [resolvedClientName] : [],
    },
    pause: !isActive || !shouldFetchResolvedClient,
  });
  const shouldFetchRealClient = Boolean(
    isImpersonating && realClientName && realClientName !== baseClientName
  );
  const [{ data: realClientData }] = useQuery<GetClientDetailsQuery>({
    query: GetClientDetailsDocument,
    variables: {
      ids: realClientName ? [realClientName] : [],
    },
    pause: !isActive || !shouldFetchRealClient,
  });

  const baseClient = baseClientData?.clients?.[0];
  const resolvedClient = resolvedClientData?.clients?.[0] ?? baseClient;
  const realClient =
    realClientName === baseClientName
      ? baseClient
      : realClientData?.clients?.[0];

  const baseReportTypes = filterDefined(
    baseClient?.reports_configuration?.report_types
  );
  const basePipelineTypes = filterDefined(
    baseClient?.data_builder_configuration?.pipeline_type
  );
  const basePostingSources = filterDefined(
    baseClient?.data_builder_configuration?.posting_source
  );

  const resolvedReportTypes =
    overrides?.reportTypes ??
    filterDefined(resolvedClient?.reports_configuration?.report_types);
  const resolvedPipelineTypes =
    overrides?.pipelineTypes ??
    filterDefined(resolvedClient?.data_builder_configuration?.pipeline_type);
  const resolvedPostingSources =
    overrides?.postingSources ??
    filterDefined(resolvedClient?.data_builder_configuration?.posting_source);

  const realReportTypes = filterDefined(
    realClient?.reports_configuration?.report_types
  );
  const realPipelineTypes = filterDefined(
    realClient?.data_builder_configuration?.pipeline_type
  );
  const realPostingSources = filterDefined(
    realClient?.data_builder_configuration?.posting_source
  );

  const resolvedReportTypeLabels = (resolvedReportTypes ?? []).map(
    (reportType) =>
      reportOptionsLookup[reportType as keyof typeof reportOptionsLookup] ??
      startCase(String(reportType))
  );
  const resolvedDatasetLabels = mapServerResponseToSelectOptions(
    resolvedPipelineTypes,
    resolvedPostingSources,
    pipelineOptions
  ).map((option) => option.label);

  const userTooltipContent = (
    <ImpersonationUserTooltipContent
      resolvedTrialUser={resolvedTrialUser}
      resolvedTrialType={resolvedTrialType}
      resolvedExpirationDate={resolvedExpirationDate}
      resolvedRole={resolvedRole}
    />
  );
  const clientTooltipContent = (
    <ImpersonationClientTooltipContent
      resolvedClientName={resolvedClientName}
      resolvedTabs={resolvedTabs}
      resolvedLinkupPostings={resolvedLinkupPostings}
      resolvedReportTypes={resolvedReportTypeLabels}
      resolvedDatasetLabels={resolvedDatasetLabels}
    />
  );

  const userSettingsDifferentFromReal = Boolean(
    isImpersonating &&
      realUser &&
      (resolvedTrialUser !== realTrialUser ||
        resolvedTrialType !== realTrialType ||
        resolvedExpirationDate !== realExpirationDate ||
        resolvedRole !== realRole)
  );
  const clientSettingsDifferentFromReal = Boolean(
    isImpersonating &&
      realUser &&
      (resolvedClientName !== realClientName ||
        resolvedLinkupPostings !== realLinkupPostings ||
        !areTabsEqual(resolvedTabs, realTabs) ||
        !areValuesEqual(resolvedReportTypes, realReportTypes) ||
        !areValuesEqual(resolvedPipelineTypes, realPipelineTypes) ||
        !areValuesEqual(resolvedPostingSources, realPostingSources))
  );
  const hasUserOverrides =
    Boolean(
      overrides &&
        ((overrides.isTrialUser !== undefined &&
          overrides.isTrialUser !== baseTrialUser) ||
          (overrides.trialType !== undefined &&
            overrides.trialType !== baseTrialType) ||
          (overrides.expirationDate !== undefined &&
            overrides.expirationDate !== baseExpirationDate) ||
          (overrides.role !== undefined && overrides.role !== baseRole))
    ) || userSettingsDifferentFromReal;
  const hasClientOverrides =
    Boolean(
      overrides &&
        ((overrides.clientName !== undefined &&
          overrides.clientName !== baseClientName) ||
          (overrides.linkupPostings !== undefined &&
            overrides.linkupPostings !== baseLinkupPostings) ||
          (overrides.tabs !== undefined &&
            !areTabsEqual(overrides.tabs, baseTabs)) ||
          (overrides.reportTypes !== undefined &&
            !areValuesEqual(overrides.reportTypes, baseReportTypes)) ||
          (overrides.pipelineTypes !== undefined &&
            !areValuesEqual(overrides.pipelineTypes, basePipelineTypes)) ||
          (overrides.postingSources !== undefined &&
            !areValuesEqual(overrides.postingSources, basePostingSources)))
    ) || clientSettingsDifferentFromReal;

  const isTrialExpired = Boolean(
    resolvedTrialUser === true &&
      resolvedExpirationDate &&
      new Date(resolvedExpirationDate) < new Date()
  );
  const isInactive = baseUser?.active === false;
  const showStatusWarning =
    (overrides || isImpersonating) && (isTrialExpired || isInactive);
  const statusWarningText = [
    isTrialExpired ? 'Trial expired' : null,
    isInactive ? 'Inactive user' : null,
  ]
    .filter(Boolean)
    .join(' • ');

  const userOverridesLabel = resolvedTrialUser ? 'Trial' : 'Live';
  const clientOverridesLabel =
    overrides?.clientName ?? (hasClientOverrides ? 'Custom' : 'Default');

  const impersonatedLabel =
    isImpersonating && impersonatedUser && !overrides
      ? impersonatedUser.email || impersonatedUser.name || 'Unknown User'
      : 'Custom';

  if (!isActive) {
    return null;
  }

  return (
    <Box
      position="fixed"
      top={2}
      left={0}
      right={0}
      zIndex={9999}
      display="flex"
      justifyContent="center"
      px={4}
      pointerEvents="none"
      data-testid="impersonation-banner"
    >
      <Alert
        status="warning"
        variant="solid"
        bg="navyBlue.500"
        color="white"
        py={1}
        px={3}
        borderRadius="md"
        boxShadow="md"
        border="1px solid"
        borderColor="green.400"
        width={{ base: '100%', md: '50%' }}
        pointerEvents="auto"
        cursor={isBannerClickable ? 'pointer' : 'default'}
        onClick={handleBannerClick}
        onKeyDown={handleBannerKeyDown}
        role={isBannerClickable ? 'button' : undefined}
        tabIndex={isBannerClickable ? 0 : undefined}
        aria-label={
          isBannerClickable ? 'Edit impersonation settings' : undefined
        }
      >
        <Flex
          width="100%"
          alignItems="center"
          justifyContent="space-between"
          flexWrap="nowrap"
          gap={2}
        >
          <Flex alignItems="center" gap={2} flex={1} minW={0} flexWrap="nowrap">
            <Flex alignItems="center" gap={1} minW={0}>
              <Box color="orange.300">
                <IconEyeOpen size={14} />
              </Box>
              <Text
                fontSize="xs"
                fontWeight="bold"
                color="green.400"
                isTruncated={true}
                maxW={{ base: '140px', md: '240px', lg: '320px' }}
                minW={0}
              >
                {impersonatedLabel}
              </Text>
            </Flex>
            {showStatusWarning && (
              <Flex
                alignItems="center"
                gap={1}
                bg="orange.300"
                color="white"
                px={2}
                py={0.5}
                borderRadius="sm"
                fontSize="xs"
                fontWeight="bold"
                whiteSpace="nowrap"
                flexShrink={0}
              >
                <WarningIcon boxSize={3} />
                <Text fontSize="xs" fontWeight="bold" color="white">
                  Warning: {statusWarningText}
                </Text>
              </Flex>
            )}

            <ImpersonationBannerOverridesTooltip
              content={userTooltipContent}
              hasOverrides={hasUserOverrides}
              label="User Overrides"
              value={userOverridesLabel}
              maxWidth={{ base: '120px', md: '200px' }}
              tooltipMaxWidth={{ base: '360px', md: '600px' }}
            />

            <ImpersonationBannerOverridesTooltip
              content={clientTooltipContent}
              hasOverrides={hasClientOverrides}
              label="Client Group Overrides"
              value={clientOverridesLabel}
              maxWidth={{ base: '140px', md: '220px' }}
              tooltipMaxWidth={{ base: '420px', md: '660px' }}
            />
          </Flex>
          <Flex alignItems="center" gap={2} flexShrink={0}>
            <Button
              size="xs"
              colorScheme="whiteAlpha"
              variant="solid"
              onClick={handleResetClick}
              fontWeight="bold"
              data-testid="reset-impersonation-button"
            >
              Reset
            </Button>
            <IconButton
              aria-label="Close impersonation banner"
              icon={<CloseIcon />}
              size="xs"
              variant="ghost"
              colorScheme="whiteAlpha"
              onClick={handleResetClick}
            />
          </Flex>
        </Flex>
      </Alert>
    </Box>
  );
}

export default ImpersonationBanner;
